// src/features/workflows/ClosureRunPage.jsx
//
// W1b: CLOSURE-WF-RUN-PAGE — Full closure run page (UI).
//
// Deep-linkable detail page for an Office Closure workflow run.
// Rendered at /workflows/runs/:id — pure consumer of the
// GET /api/workflows/runs/:id response (W1a). Never recomputes fact state.
//
// Sections:
//   - Breadcrumb (Workflows → run name)
//   - Header block: title, chips, reactive-origin sentence, conservation banner
//   - "Do next" card (first failing check)
//   - Phase + step list with fact-indicator badges
//   - Inline "Mark notified" action (editor/admin only, on the manual step)

import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Lock, CheckCircle2, AlertCircle, Clock, Circle, ChevronRight } from "lucide-react";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { getWorkflowRun, markWorkflowSubstepNotified } from "../../data/workflowsClient.js";
import { log } from "../../utils/logger.js";
import { showToast } from "../../components/toastService.jsx";
import { buildLocationUrl } from "../../services/buildUrl.js";

// ─── Role gate ───────────────────────────────────────────────────────────────

const CAN_EDIT = new Set(["editor", "admin"]);

// ─── Date helper ─────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return "—";
  const s = typeof d === "string" ? d : String(d);
  const datePart = s.includes("T") ? s.slice(0, s.indexOf("T")) : s;
  const parts = datePart.split("-").map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return datePart;
  const [y, m, day] = parts;
  return new Date(y, m - 1, day).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
}

// ─── Health chip colors ───────────────────────────────────────────────────────

function healthChipStyle(health) {
  if (!health) return { bg: T.bg, fg: T.mid };
  if (health === "Needs attention") return { bg: T.redSoft, fg: T.red };
  if (health === "Waiting on a team") return { bg: T.ragAmberBg, fg: T.ragAmberText };
  return { bg: T.greenSoft, fg: T.green };
}

// ─── Chip ─────────────────────────────────────────────────────────────────────

function Chip({ label, bg, fg, testId }) {
  return (
    <span
      data-testid={testId}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        borderRadius: 6, padding: "2px 10px",
        fontSize: T.fsXs, fontWeight: T.fwSemibold,
        background: bg, color: fg,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// ─── Fact-indicator badge ─────────────────────────────────────────────────────
//
// Accepts a check result object from run.checks and produces the badge.
// Variants (per spec):
//   auto_fact + pass    → green   "✓ Auto-verified · {factLabel}"
//   auto_fact + fail    → red     "✗ Auto-check · {factLabel}"
//   waiting_on_team + * → amber   "◷ Waiting on · {factLabel}" (pass→same as auto pass)
//   manual + pass       → green   "✓ Notified"
//   manual (locked)     → grey    "⊙ Locked"
//   manual (open)       → grey    "◯ Manual"

export function factIndicatorProps(check) {
  if (!check) return { label: "—", bg: T.bg, fg: T.mid };

  const { kind, state, factLabel, locked } = check;

  if (kind === "auto_fact") {
    if (state === "pass") {
      return {
        label: `✓ Auto-verified${factLabel ? ` · ${factLabel}` : ""}`,
        bg: T.greenSoft,
        fg: T.green,
      };
    }
    return {
      label: `✗ Auto-check${factLabel ? ` · ${factLabel}` : ""}`,
      bg: T.redSoft,
      fg: T.red,
    };
  }

  if (kind === "waiting_on_team") {
    if (state === "pass") {
      return {
        label: `✓ Auto-verified${factLabel ? ` · ${factLabel}` : ""}`,
        bg: T.greenSoft,
        fg: T.green,
      };
    }
    return {
      label: `◷ Waiting on${factLabel ? ` · ${factLabel}` : " team"}`,
      bg: T.ragAmberBg,
      fg: T.ragAmberText,
    };
  }

  if (kind === "manual") {
    if (state === "pass") {
      return { label: "✓ Notified", bg: T.greenSoft, fg: T.green };
    }
    if (locked) {
      return { label: "⊙ Locked", bg: T.bg, fg: T.mid };
    }
    return { label: "◯ Manual · pending confirmation", bg: T.bg, fg: T.mid };
  }

  return { label: "—", bg: T.bg, fg: T.mid };
}

function FactBadge({ check, testId }) {
  const { label, bg, fg } = factIndicatorProps(check);
  return (
    <span
      data-testid={testId}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        fontSize: T.fsXxs, fontWeight: T.fwSemibold,
        background: bg, color: fg,
        borderRadius: 6, padding: "2px 8px",
        marginTop: 6,
      }}
    >
      {label}
    </span>
  );
}

// ─── Step marker icon ─────────────────────────────────────────────────────────

function StepMarker({ check }) {
  const size = 22;
  const base = {
    width: size, height: size, borderRadius: "50%", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    marginTop: 1,
  };

  if (!check) {
    return <div style={{ ...base, background: T.bg, border: `2px solid ${T.border}` }} />;
  }

  const { kind, state, locked } = check;

  if (state === "pass") {
    return (
      <div style={{ ...base, background: T.green }}>
        <CheckCircle2 size={14} color={T.white} strokeWidth={2.5} />
      </div>
    );
  }
  if (kind === "auto_fact" && state === "fail") {
    return (
      <div style={{ ...base, background: T.red }}>
        <AlertCircle size={14} color={T.white} strokeWidth={2.5} />
      </div>
    );
  }
  if (kind === "waiting_on_team" && state === "waiting") {
    return (
      <div style={{ ...base, background: T.negotiateOrange }}>
        <Clock size={14} color={T.white} strokeWidth={2.5} />
      </div>
    );
  }
  if (kind === "manual" && locked) {
    return (
      <div style={{ ...base, background: T.bg, border: `2px solid ${T.border}` }}>
        <Lock size={11} color={T.mid} />
      </div>
    );
  }
  return (
    <div style={{ ...base, background: T.white, border: `2px solid ${T.border}` }}>
      <Circle size={10} color={T.light} />
    </div>
  );
}

// ─── Step card tint ───────────────────────────────────────────────────────────

function stepCardStyle(check) {
  if (!check) return { background: T.card, border: `1px solid ${T.border}` };
  const { kind, state, locked } = check;
  if (kind === "auto_fact" && state === "fail") {
    return { background: T.redSoft, border: `1px solid ${T.ragRedBorder}` };
  }
  if (kind === "waiting_on_team" && state === "waiting") {
    return { background: T.utilAmber, border: `1px solid ${T.utilAmberBorder}` };
  }
  if (kind === "manual" && locked) {
    return { background: T.card, border: `1px solid ${T.border}`, opacity: 0.55 };
  }
  return { background: T.card, border: `1px solid ${T.border}` };
}

// ─── Object deep-link map ─────────────────────────────────────────────────────
//
// Maps each checkId to a deep-link within the app.
// Uses buildLocationUrl / buildMarketPlanUrl patterns.

function buildCheckLink(checkId, sourceLocationId, targetLocationId) {
  if (!checkId) return null;
  switch (checkId) {
    case 1: return sourceLocationId ? buildLocationUrl(sourceLocationId) : null;
    case 3: return sourceLocationId ? buildLocationUrl(sourceLocationId, "space") : null;
    case 4: return targetLocationId ? buildLocationUrl(targetLocationId, "space") : null;
    case 5: return sourceLocationId ? buildLocationUrl(sourceLocationId, "lease") : null;
    case 6: return `/markets`;
    case 7: return `/admin`;
    default: return null;
  }
}

function buildCheckLinkLabel(checkId) {
  switch (checkId) {
    case 1: return "Offices → source office ↗";
    case 3: return "Offices → source office → Capacity ↗";
    case 4: return "Offices → target office → Capacity ↗";
    case 5: return "Leases → source office ↗";
    case 6: return "Market Plan ↗";
    case 7: return "Data & Headcount ↗";
    default: return null;
  }
}

// Per-check human descriptions (shown below the step title).
const CHECK_DESCRIPTIONS = {
  1: "The source office must have its stage set to Vacated before the closure can proceed.",
  2: "Notify the Workday team that the office is closing and people are relocating, so they can remap headcount.",
  3: "Add a 0-seat capacity row for the source office effective the close date to release those seats from the portfolio.",
  4: "The target office must have a capacity row added (effective on or after the move date) showing seats above the pre-move baseline.",
  5: "Set the source office lease status to Vacated or Vacant-Held to reflect the lease is no longer in use.",
  6: "Update the Market Plan disposition for the source office to Sublease out.",
  7: "Verify that Workday has remapped the headcount - source headcount should be 0 and target headcount should have increased.",
  8: "All prior checks pass and the conservation balance is verified. Mark done to formally close the run.",
};

// ─── Inline "Mark notified" action ───────────────────────────────────────────

function MarkNotifiedAction({ runId, substepId, onDone }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await markWorkflowSubstepNotified(runId, substepId, note.trim() || null);
      showToast("Workday notified.", { type: "success" });
      onDone();
    } catch (err) {
      log.error("MarkNotifiedAction: PATCH failed", err);
      setError(err?.message ?? "Failed to mark notified - please try again.");
      setSaving(false);
    }
  }, [runId, substepId, note, onDone]);

  if (!open) {
    return (
      <button
        type="button"
        data-testid="mark-notified-btn"
        onClick={() => setOpen(true)}
        style={{
          border: "none", borderRadius: 8, background: T.blue, color: T.white,
          padding: "6px 13px", fontSize: T.fsXs, fontWeight: T.fwBold,
          cursor: "pointer", flexShrink: 0, alignSelf: "flex-start",
        }}
      >
        Mark notified
      </button>
    );
  }

  return (
    <div
      data-testid="mark-notified-panel"
      style={{
        marginTop: 10, padding: "12px 14px", background: T.bg,
        border: `1px solid ${T.border}`, borderRadius: 9,
        display: "flex", flexDirection: "column", gap: 8,
      }}
    >
      <div style={{ fontSize: T.fsXs, color: T.dark, fontWeight: T.fwSemibold }}>
        Mark Workday notified
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional note (e.g. email thread link, date contacted)"
        data-testid="mark-notified-note"
        style={{
          width: "100%", border: `1px solid ${T.border}`, borderRadius: 7,
          padding: "8px 10px", fontSize: T.fsXs, color: T.dark,
          resize: "vertical", minHeight: 60, fontFamily: "inherit",
        }}
      />
      {error && (
        <div style={{ fontSize: T.fsXs, color: T.red }}>{error}</div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          data-testid="mark-notified-confirm"
          onClick={handleConfirm}
          disabled={saving}
          style={{
            border: "none", borderRadius: 7, background: T.green, color: T.white,
            padding: "6px 13px", fontSize: T.fsXs, fontWeight: T.fwBold,
            cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? "Saving…" : "Confirm"}
        </button>
        <button
          type="button"
          data-testid="mark-notified-cancel"
          onClick={() => { setOpen(false); setNote(""); setError(null); }}
          style={{
            border: `1px solid ${T.border}`, borderRadius: 7, background: T.card,
            color: T.mid, padding: "6px 13px", fontSize: T.fsXs,
            fontWeight: T.fwSemibold, cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Conservation banner ──────────────────────────────────────────────────────

function ConservationBanner({ conservation }) {
  const { peopleToMove, sourceSeats, targetAbsorbed, balanced } = conservation;

  const cells = [
    {
      label: "People to move",
      value: peopleToMove !== null ? String(peopleToMove) : "—",
      bad: peopleToMove !== null && peopleToMove > 0,
    },
    {
      label: "Seats - source",
      value: sourceSeats !== null ? String(sourceSeats) : "—",
      bad: sourceSeats !== null && sourceSeats > 0,
    },
    {
      label: "Seats - target absorbed",
      value: targetAbsorbed !== null ? `+${targetAbsorbed}` : "—",
      ok: targetAbsorbed !== null && targetAbsorbed > 0,
    },
    {
      label: "Conservation",
      value: balanced ? "Balanced" : "Unbalanced",
      ok: balanced,
      bad: !balanced,
    },
  ];

  return (
    <div
      data-testid="conservation-banner"
      style={{
        display: "flex", border: `1px solid ${T.border}`,
        borderRadius: 12, overflow: "hidden", marginTop: 16,
      }}
    >
      {cells.map((c, i) => (
        <div
          key={c.label}
          data-testid={`conservation-cell-${i}`}
          style={{
            flex: 1, padding: "12px 14px",
            borderLeft: i > 0 ? `1px solid ${T.border}` : "none",
          }}
        >
          <div style={{
            fontSize: T.fsXxs, textTransform: "uppercase",
            letterSpacing: 0.5, color: T.mid, fontWeight: T.fwBold, marginBottom: 4,
          }}>
            {c.label}
          </div>
          <div style={{
            fontSize: T.fsMd5, fontWeight: T.fwBold,
            color: c.ok ? T.green : c.bad ? T.red : T.dark,
          }}>
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── "Do next" card ───────────────────────────────────────────────────────────

function DoNextCard({ check, run }) {
  const location = useLocation();
  if (!check) return null;
  if (check.state !== "fail") return null;

  const link = buildCheckLink(check.checkId, run.sourceLocationId, run.targetLocationId);
  const linkLabel = link ? "Fix →" : null;

  return (
    <div
      data-testid="do-next-card"
      style={{
        border: `1px solid ${T.ragRedBorder}`, background: T.redSoft,
        borderRadius: 13, padding: "14px 16px",
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        gap: 14, marginBottom: 18,
      }}
    >
      <div>
        <div style={{ fontSize: T.fsXs, color: T.red, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>
          Do next
        </div>
        <div style={{ fontSize: T.fsSm5, color: T.dark, fontWeight: T.fwSemibold }}>
          {check.label}
        </div>
        {check.factLabel && (
          <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 3 }}>
            {check.factLabel}
          </div>
        )}
      </div>
      {link && (
        <Link
          to={link}
          state={{ ...location.state, returnPath: `${location.pathname}${location.search}${location.hash}` }}
          data-testid="do-next-fix-btn"
          style={{
            display: "inline-block", border: "none", borderRadius: 8,
            background: T.blue, color: T.white,
            padding: "7px 13px", fontSize: T.fsXs, fontWeight: T.fwBold,
            cursor: "pointer", textDecoration: "none", whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {linkLabel}
        </Link>
      )}
    </div>
  );
}

// ─── Phase header ─────────────────────────────────────────────────────────────

function PhaseHeader({ name, doneCount, totalCount }) {
  return (
    <div style={{
      fontSize: T.fsXs, fontWeight: T.fwBold, color: T.mid,
      textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8,
    }}>
      {name} · {doneCount} of {totalCount} steps done
    </div>
  );
}

// ─── Step card ────────────────────────────────────────────────────────────────

function StepCard({ substep, check, run, canEdit, onCheckUpdated }) {
  const location = useLocation();
  const cardStyle = stepCardStyle(check);
  const link = check ? buildCheckLink(check.checkId, run.sourceLocationId, run.targetLocationId) : null;
  const linkLabel = check ? buildCheckLinkLabel(check.checkId) : null;
  const desc = check ? (CHECK_DESCRIPTIONS[check.checkId] ?? substep.label) : substep.label;

  const isManualNotify = check?.kind === "manual" && check?.state !== "pass" && !check?.locked;
  const showFixBtn = check?.kind === "auto_fact" && check?.state === "fail" && link;

  return (
    <div
      data-testid={`step-card-${substep.id}`}
      style={{
        ...cardStyle,
        borderRadius: 11, padding: "12px 14px",
        display: "flex", alignItems: "flex-start", gap: 12,
        marginBottom: 8,
      }}
    >
      <StepMarker check={check} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark }}>
          {substep.label}
        </div>
        <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 3, lineHeight: 1.5 }}>
          {desc}
          {link && (
            <>
              {" "}
              <Link
                to={link}
                state={{ ...location.state, returnPath: `${location.pathname}${location.search}${location.hash}` }}
                style={{ color: T.blue, fontWeight: T.fwSemibold, textDecoration: "none", fontSize: T.fsXxs }}
              >
                {linkLabel}
              </Link>
            </>
          )}
        </div>
        <FactBadge check={check} testId={`fact-badge-${substep.id}`} />

        {isManualNotify && canEdit && (
          <MarkNotifiedAction
            runId={run.id}
            substepId={substep.id}
            onDone={onCheckUpdated}
          />
        )}
      </div>

      {showFixBtn && (
        <Link
          to={link}
          state={{ ...location.state, returnPath: `${location.pathname}${location.search}${location.hash}` }}
          data-testid={`fix-btn-${substep.id}`}
          style={{
            display: "inline-block", border: "none", borderRadius: 8,
            background: T.blue, color: T.white,
            padding: "6px 13px", fontSize: T.fsXs, fontWeight: T.fwBold,
            cursor: "pointer", textDecoration: "none", whiteSpace: "nowrap",
            flexShrink: 0, alignSelf: "center",
          }}
        >
          Fix
        </Link>
      )}
    </div>
  );
}

// ─── ClosureRunPage ───────────────────────────────────────────────────────────

export function ClosureRunPage({ currentRole = "viewer" }) {
  const { id: runId } = useParams();
  const canEdit = CAN_EDIT.has(currentRole);

  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!runId) return;
    setLoading(true);
    setError(null);
    try {
      const detail = await getWorkflowRun(runId);
      setRun(detail);
    } catch (err) {
      log.error("ClosureRunPage: failed to load run", { runId, err: String(err) });
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div
        data-testid="closure-run-loading"
        style={{
          padding: "var(--space-page-y, 22px) var(--space-page-x, 28px)",
          fontSize: T.fsSm, color: T.mid,
        }}
      >
        Loading run…
      </div>
    );
  }

  // ── Error / 404 ────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div style={{ padding: "var(--space-page-y, 22px) var(--space-page-x, 28px)" }}>
        <div style={{ marginBottom: 12 }}>
          <Link to="/workflows" style={{ color: T.blue, fontSize: T.fsXs, fontWeight: T.fwSemibold, textDecoration: "none" }}>
            ‹ Actions
          </Link>
        </div>
        <ErrorBanner error={error} onDismiss={load} />
      </div>
    );
  }

  if (!run) return null;

  // ── Derived values ─────────────────────────────────────────────────────────

  // Build check lookup by label (for matching with substep labels)
  const checksByLabel = {};
  for (const c of run.checks ?? []) {
    checksByLabel[c.label] = c;
  }

  // "Do next" = first failing auto_fact check
  const firstFailingCheck = (run.checks ?? []).find((c) => c.state === "fail");

  // Health chip
  const hStyle = healthChipStyle(run.health);

  // Reactive origin sentence
  const reactiveOrigin = run.sourceType === "location"
    ? `This run was started for office ${run.sourceLabel || run.locationName || "the source office"}.`
    : run.sourceLabel
    ? `This run was launched from ${run.sourceLabel}.`
    : null;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      data-testid="closure-run-page"
      style={{
        padding: "22px 28px 60px",
        maxWidth: 880,
      }}
    >
      {/* Breadcrumb */}
      <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 6, fontSize: T.fsXs }}>
        <Link
          to="/workflows"
          data-testid="breadcrumb-workflows"
          style={{ color: T.blue, fontWeight: T.fwBold, textDecoration: "none" }}
        >
          Actions
        </Link>
        <ChevronRight size={12} color={T.mid} />
        <span style={{ color: T.dark, fontWeight: T.fwSemibold }}>
          {run.name}
        </span>
      </div>

      {/* Header block */}
      <div
        data-testid="closure-run-header"
        style={{
          background: T.card, border: `1px solid ${T.border}`,
          borderRadius: 15, padding: "18px 20px", marginBottom: 16,
        }}
      >
        <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 10 }}>
          {run.name}
        </div>

        {/* Chip row */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
          {run.sourceLabel && (
            <Chip
              testId="chip-launched-from"
              label={`↳ Launched from · ${run.sourceLabel}`}
              bg={T.blueSoft}
              fg={T.blue}
            />
          )}
          {run.ownerName && (
            <Chip
              testId="chip-owner"
              label={`Owner · ${run.ownerName}`}
              bg={T.bg}
              fg={T.mid}
            />
          )}
          {run.health && (
            <Chip
              testId="chip-health"
              label={run.health}
              bg={hStyle.bg}
              fg={hStyle.fg}
            />
          )}
          {(run.targetCompletion || run.dueDate) && (
            <Chip
              testId="chip-effective-date"
              label={`Effective ${fmtDate(run.targetCompletion || run.dueDate)}`}
              bg={T.bg}
              fg={T.mid}
            />
          )}
        </div>

        {/* Reactive origin sentence */}
        {reactiveOrigin && (
          <div data-testid="reactive-origin" style={{ fontSize: T.fsXs, color: T.mid, marginTop: 6, lineHeight: 1.5 }}>
            {reactiveOrigin}
          </div>
        )}

        {/* Conservation banner — only for closure runs */}
        {run.conservation && (
          <ConservationBanner conservation={run.conservation} />
        )}
      </div>

      {/* "Do next" card */}
      {firstFailingCheck && (
        <DoNextCard check={firstFailingCheck} run={run} />
      )}

      {/* Phase + step list */}
      <div data-testid="phase-step-list">
        {(run.phases ?? []).map((phase) => {
          const substeps = phase.substeps ?? [];
          const doneCount = substeps.filter((s) => {
            const c = checksByLabel[s.label];
            return c ? c.state === "pass" : s.status === "done";
          }).length;

          return (
            <div key={phase.id} data-testid={`phase-${phase.id}`} style={{ marginBottom: 24 }}>
              <PhaseHeader
                name={phase.name}
                doneCount={doneCount}
                totalCount={substeps.length}
              />
              {substeps.map((substep) => {
                const check = checksByLabel[substep.label] ?? null;
                return (
                  <StepCard
                    key={substep.id}
                    substep={substep}
                    check={check}
                    run={run}
                    canEdit={canEdit}
                    onCheckUpdated={load}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
